import { addDay } from "../runsheets/util";

/* Pay-period maths and the small formatting helpers the timesheet screens
 * share. The server is the authority on every number here - these only
 * format what it returns, because paid hours carry legal weight (ERA s130 /
 * Holidays Act s81) and two implementations would eventually disagree. */

// Mirrors the backend default PAY_FORTNIGHT_ANCHOR. Only used to pick the
// window to show first when /time/periods comes back empty; once the server
// lists its periods, its dates win.
const ANCHOR = "2026-09-07";

const dayNum = (d) => Math.floor(Date.parse(`${String(d).slice(0, 10)}T00:00:00Z`) / 86400000);

export const currentFortnight = (today) => {
  const index = Math.floor((dayNum(today) - dayNum(ANCHOR)) / 14);
  const start = addDay(ANCHOR, index * 14);
  return { start, end: addDay(start, 13) };
};

export const fortnightDays = (start) =>
  Array.from({ length: 14 }, (_, i) => addDay(start, i));

// "Mon" over "08/09" - the grid has 14 of these and no room for a full date.
export const dayHead = (d) => {
  try {
    const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return { dow: "", dm: String(d).slice(5) };
    return {
      dow: dt.toLocaleDateString("en-NZ", { weekday: "short" }),
      dm: dt.toLocaleDateString("en-NZ", { day: "2-digit", month: "2-digit" }),
      weekend: dt.getDay() === 0 || dt.getDay() === 6,
    };
  } catch {
    return { dow: "", dm: String(d).slice(5) };
  }
};

// Meal breaks are PAID: paid hours are finish - start and break minutes are
// never deducted. They are recorded because work time (not pay) is what the
// 5.5h break rule is measured against.
export const fmtHours = (h) => {
  const n = Number(h);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

export const DAY_TYPES = [
  { value: "normal", label: "Normal" },
  { value: "public_holiday", label: "Public holiday" },
  { value: "annual_leave", label: "Annual leave" },
  { value: "sick", label: "Sick" },
  { value: "bereavement", label: "Bereavement" },
  { value: "unpaid", label: "Unpaid" },
  { value: "not_rostered", label: "Not rostered" },
];

// Flags read "OVER LIMIT" (red) or "CHECK" (amber). Nothing in this screen
// ever prints "compliant": the dashboard is not a logbook and cannot certify
// work time, it can only show whether the hours recorded here raised a flag.
// The server's tiers are over_limit / check / info - info is the MANUAL note,
// which is a provenance marker rather than a work-time flag.
export const tierOf = (flags) => {
  const list = flags || [];
  if (list.some((f) => f.tier === "over_limit")) return "red";
  if (list.some((f) => f.tier === "check")) return "amber";
  return null;
};

export const FLAG_TINT = {
  over_limit: "text-red-300 border-red-500/40 bg-red-500/10",
  check: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  info: "text-violet-300 border-violet-500/40 bg-violet-500/10",
};

export const CELL_TINT = {
  red: "bg-red-500/15 text-red-200 border-red-500/40",
  amber: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  manual: "bg-violet-500/15 text-violet-200 border-violet-500/40",
};

export const LEGAL_LINE = "Pay record. Not a logbook under Land Transport Rule 62001.";

const ROLE_ORDER = { driver: 0, offsider: 1 };

// A shift's recorded breaks as "12:00–12:30" strings, in the server's order
// (it sorts by started_at). The start/finish are the server's NZ "HH:MM"
// strings; a break still running has no finish and reads "15:00–open".
// Empty when the day only has a legacy minutes total, so callers can fall
// back to break_minutes.
export const breakSpans = (shift) =>
  (shift?.breaks || []).map((b) => `${b.start || "?"}–${b.open || !b.finish ? "open" : b.finish}`);

const cellTooltip = (cell) => {
  const lines = cell.shifts.map((s) => {
    const span = `${s.start || "?"}–${s.finish || "open"}`;
    const spans = breakSpans(s);
    const brk = spans.length
      ? ` · break ${spans.join(", ")} · ${s.break_minutes || 0} min (paid)`
      : s.break_minutes ? ` · ${s.break_minutes} min break (paid, no times)` : "";
    const typed = s.source === "manual" ? ` · keyed in by ${s.entered_by || "the office"}` : "";
    return `${span} · ${fmtHours(s.paid_hours)}h${brk}${typed}`;
  });
  cell.shifts.forEach((s) => (s.flags || []).forEach((f) => lines.push(f.message)));
  return lines.join("\n");
};

/* One grid row per crew member with a cell per NZ work_date. Hours, flags and
 * paid totals all come from the server - this only groups them, because the
 * numbers are pay and a second implementation of them would eventually
 * disagree with the payslip. */
export function buildRows(crew, shifts, days) {
  const inRange = new Set(days);
  const byPerson = new Map();
  const add = (id, name, role) => {
    const row = { id: String(id), name: name || "(unnamed)", role: role || "crew", cells: {}, total: 0 };
    byPerson.set(String(id), row);
    return row;
  };
  (crew || []).forEach((c) => add(c.id, c.name, c.role));

  (shifts || []).forEach((s) => {
    // A shift whose crew row was deleted still has to appear: the hours were
    // worked and still have to be paid.
    const row = byPerson.get(String(s.person_id))
      || add(s.person_id, s.person_name || "Removed from roster", s.role);
    const day = String(s.work_date || "").slice(0, 10);
    if (!inRange.has(day)) return;
    const cell = row.cells[day] || (row.cells[day] = { hours: 0, shifts: [], tier: null, manual: false });
    cell.shifts.push(s);
    cell.hours += Number(s.paid_hours) || 0;
    cell.manual = cell.manual || s.source === "manual";
    row.total += Number(s.paid_hours) || 0;
  });

  byPerson.forEach((row) => {
    Object.values(row.cells).forEach((cell) => {
      cell.tier = tierOf(cell.shifts.flatMap((s) => s.flags || []));
      cell.tooltip = cellTooltip(cell);
    });
    row.total = Math.round(row.total * 100) / 100;
  });

  return Array.from(byPerson.values()).sort((a, b) =>
    (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || String(a.name).localeCompare(String(b.name)));
}

const TIER_RANK = { over_limit: 0, check: 1, info: 2 };

// Every flag raised in the window, worst first then newest day - the operator
// works these before approving, and a flag buried in a tooltip is not worked.
export function exceptionList(shifts) {
  const out = [];
  (shifts || []).forEach((s) => (s.flags || []).forEach((f) => out.push({ ...f, shift: s })));
  return out.sort((a, b) =>
    (TIER_RANK[a.tier] ?? 9) - (TIER_RANK[b.tier] ?? 9)
    || String(b.shift.work_date).localeCompare(String(a.shift.work_date)));
}
